import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input';

import AddressAutocomplete from '@/components/address-autocomplete';
import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

import useMercadoEnvíosConfig from './useMercadoEnvíosConfig';

const PLATFORM_NAME = 'MercadoLibreFlex';

interface MercadoEnviosConfigProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  pluginId: string;
  clientId: string;
  activeMlCount: number;
  webhookPlatformOwner: string | null;
  onSaved?: () => void;
}

const MercadoEnviosConfig = ({
  isOpen,
  onClose,
  name,
  pluginId,
  clientId,
  activeMlCount,
  webhookPlatformOwner,
  onSaved,
}: MercadoEnviosConfigProps) => {
  const theme = useTheme();
  const hook = useMercadoEnvíosConfig({ pluginId, clientId, isOpen });

  const handleSave = async () => {
    const success = await hook.savePluginConfig();
    if (success) {
      onSaved?.();
      onClose();
    }
  };
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Configuración de conexión ${name}`}
        maxWidth="lg"
        actions={
          <Button variant="contained" onClick={handleSave} disabled={hook.saving}>
            {hook.saving ? 'Guardando...' : 'Guardar'}
          </Button>
        }
      >
        <Grid container size={12} flexDirection="column" spacing={2}>
          <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
            Dirección de recolección
          </Typography>
          <Grid size={12}>
            <Box sx={{ px: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                <Typography
                  color={
                    !hook.useCustomAddress ? theme.palette.primary.main : theme.palette.grey[500]
                  }
                  fontWeight={!hook.useCustomAddress ? 'bold' : 'normal'}
                >
                  Usar dirección de Mercado Libre
                </Typography>
                <Switch
                  checked={hook.useCustomAddress}
                  onChange={hook.handleToggleAddress}
                  color="primary"
                />
                <Typography
                  color={
                    hook.useCustomAddress ? theme.palette.primary.main : theme.palette.grey[500]
                  }
                  fontWeight={hook.useCustomAddress ? 'bold' : 'normal'}
                >
                  Usar dirección diferente
                </Typography>
              </Box>
              {!hook.useCustomAddress && (
                <Typography
                  variant="caption"
                  color={theme.palette.grey[400]}
                  mt={1}
                  display="block"
                >
                  (Dirección predeterminada cargada automáticamente)
                </Typography>
              )}
              {hook.useCustomAddress && (
                <Grid size={{ xxs: 12 }} mt={2}>
                  <AddressAutocomplete
                    size="medium"
                    label="Nueva dirección de recolección"
                    placeholder="Dirección"
                    value={hook.addressData.address}
                    country={hook.addressData.country || 'MEX'}
                    onChange={hook.handleChangeAddressText}
                    onSelectAddress={hook.handleSelectAddress}
                  />
                  {hook.addressData.address && (
                    <Typography
                      variant="caption"
                      color={theme.palette.grey[400]}
                      mt={0.5}
                      display="block"
                    >
                      {hook.addressData.zipCode && `CP: ${hook.addressData.zipCode}`}
                    </Typography>
                  )}
                </Grid>
              )}
            </Box>
          </Grid>
          <Typography variant="body1" color={theme.palette.grey[500]} mt={2} fontWeight="bold">
            Información de origen
          </Typography>
          <Box sx={{ px: 2, width: '100%' }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Nombre de tienda"
                  value={hook.storeInfo.storeName}
                  onChange={(e) => hook.handleStoreInfo('storeName', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email de tienda"
                  value={hook.storeInfo.storeEmail}
                  onChange={(e) => hook.handleStoreInfo('storeEmail', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={12}>
                <MuiTelInput
                  fullWidth
                  size="small"
                  label="Teléfono de tienda"
                  defaultCountry="MX"
                  onlyCountries={['MX', 'CO', 'CL', 'PE']}
                  value={hook.storeInfo.storePhone}
                  onChange={hook.handlePhone}
                  error={hook.phoneError}
                  helperText={hook.phoneError ? 'Ingresa un número válido' : undefined}
                />
              </Grid>
            </Grid>
          </Box>

          <Typography variant="body1" color={theme.palette.grey[500]} mt={2} fontWeight="bold">
            Configuraciones adicionales
          </Typography>
          <Box sx={{ px: 2 }}>
            <FormControlLabel
              sx={{ gap: '1rem' }}
              control={
                <Checkbox
                  onChange={() => hook.handleCheckOptions('createOrders')}
                  color="primary"
                  name="createOrders"
                  checked={hook.checkOptions.createOrders}
                />
              }
              label={
                <Typography color={theme.palette.grey[500]}>
                  Creación automática de orden
                </Typography>
              }
            />
            {activeMlCount > 1 &&
              (() => {
                const isDisabled = !!webhookPlatformOwner && webhookPlatformOwner !== PLATFORM_NAME;
                return (
                  <Tooltip
                    title={
                      isDisabled
                        ? 'Desactiva esta opción en la plataforma primaria actual antes de cambiarla'
                        : 'Si tienes varias plataformas activas, esta se priorizará para la creación automática de órdenes'
                    }
                    placement="right"
                  >
                    <span>
                      <FormControlLabel
                        sx={{ gap: '1rem' }}
                        control={
                          <Checkbox
                            color="primary"
                            checked={hook.isWebhookPrimary}
                            onChange={(e) => hook.setIsWebhookPrimary(e.target.checked)}
                            disabled={isDisabled}
                          />
                        }
                        label={
                          <Typography
                            color={isDisabled ? theme.palette.grey[400] : theme.palette.grey[500]}
                          >
                            Plataforma primaria
                          </Typography>
                        }
                      />
                    </span>
                  </Tooltip>
                );
              })()}
          </Box>

          <Typography variant="body1" color={theme.palette.grey[500]} mt={2} fontWeight="bold">
            Seguro adicional
          </Typography>
          <Box sx={{ px: 2, width: '100%' }}>
            <FormControlLabel
              sx={{ gap: '1rem' }}
              control={
                <Switch
                  checked={hook.additionalInsurance}
                  onChange={hook.handleToggleAdditionalInsurance}
                  color="primary"
                />
              }
              label={
                <Typography color={theme.palette.grey[500]}>
                  Asignar seguro adicional automáticamente
                </Typography>
              }
            />
            {hook.additionalInsurance && (
              <Grid container spacing={2} mt={1}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monto límite del valor de la venta"
                    value={hook.insuranceLimit}
                    onChange={(e) => hook.handleInsuranceLimit(e.target.value)}
                    error={hook.insuranceLimitError}
                    helperText={
                      hook.insuranceLimitError
                        ? 'Ingresa un monto mayor a 0'
                        : 'Las ventas menores o iguales a este monto se generan sin seguro adicional'
                    }
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      },
                      htmlInput: { inputMode: 'decimal' },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>
      </Modal>
    </>
  );
};

export default MercadoEnviosConfig;
